<template>
    <div>
        <div class="w-full md:w-4/5 m-auto py-6 px-0 md:px-12">
            <div v-if="!loading" class="lds-spinner">
                <div /><div /><div /><div /><div /><div /><div /><div /><div /><div /><div /><div />
            </div>
            <h1 class="mb-4 text-green-600 text-2xl text-left font-bold text-2xl">
                Review Application
                / {{ personalData.first_name }} {{ personalData.last_name }}
            </h1>
            <div class="bg-white bg-gray-100 border px-2 py-2 mt-1 pb-6">
                <div class="flex justify-between mb-6 px-8 mt-4 align-baseline text-sm">
                    <div v-if="loading" class="w-full lg:flex text-left">
                        <div class="w-full flex lg:w-1/2">
                            <div>
                                <div>Applicant:</div>
                                <div>Programme:</div>
                                <div>Programme type:</div>
                            </div>
                            <div class="ml-4">
                                <div>{{ personalData.first_name }} {{ personalData.last_name }}</div>
                                <div v-if="applicationData.activeChoice">{{ applicationData.activeChoice.data?.programme_title }}</div>
                                <div v-else>&nbsp;</div>
                                <div v-if="firstChoiceType">{{ firstChoiceType.name ?? '' }}</div>
                                <div v-else>&nbsp;</div>
                            </div>
                        </div>
                        <div class="w-full flex lg:w-1/2">
                            <div>
                                <div>Academic year:</div>
                                <div>Study mode:</div>
                                <div>Status:</div>
                            </div>
                            <div class="ml-4">
                                <div>{{ applicationData.academicYear }}</div>
                                <div>{{ applicationData.activeChoice?.data?.mode_of_study_name }}</div>
                                <div>{{ applicationData.public_status }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="applicationData.hasDocumentRequest">
                    <document-request-response-form :document-requests="applicationData?.documentRequests?.data"/>
                </div>

                <div class="w-full flex justify-end" @click="expand">
                    <div v-if="!expandAll" class="text-sm leading-10 cursor-pointer">Expand all</div>
                    <div v-else class="text-sm leading-10 cursor-pointer">Collapse all</div>
                    <icon :name="!expandAll ? 'plus-circle' : 'minus-circle'" class="w-7 h-10 ml-2 cursor-pointer" />
                </div>

                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="programmeDetailsShow = !programmeDetailsShow"
                >
                    <h1 class="text-1xl leading-10">Programme Details</h1>
                    <icon :name="!programmeDetailsShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !programmeDetailsShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <div v-for="(programme, index) in programmeData.data" :key="index">
                            <h1 class="text-1xl py-2 text-green-600 font-bold">Programme choice {{ programme.choice }}</h1>
                            <table class="mb-8">
                                <tbody>
                                    <tr>
                                        <th>Programme Type:</th>
                                        <td>{{ programme.programme_type }}</td>
                                    </tr>
                                    <tr>
                                        <th>Mode of study:</th>
                                        <td>{{ programme.programmeInstance.data.moa_name }}</td>
                                    </tr>
                                    <tr>
                                        <th>Programme title:</th>
                                        <td>{{ programme.programmeInstance.data.programme_title }}</td>
                                    </tr>
                                    <tr v-if="applicationData.proposed_start_date">
                                        <th>Proposed start date:</th>
                                        <td>{{ applicationData.proposed_start_date }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>


                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="personalDetailsShow = !personalDetailsShow"
                >
                    <h1 class="text-1xl leading-10">Personal Details</h1>
                    <icon :name="!personalDetailsShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !personalDetailsShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <table>
                            <tbody>
                                <tr>
                                    <th>Title:</th>
                                    <td>{{ personalData.title.data.name ?? '' }}</td>
                                </tr>
                                <tr>
                                    <th>First name:</th>
                                    <td>{{ personalData.first_name }}</td>
                                </tr>
                                <tr>
                                    <th>Middle name 1:</th>
                                    <td>{{ personalData.middle_name1 }}</td>
                                </tr>
                                <tr>
                                    <th>Middle name 2:</th>
                                    <td>{{ personalData.middle_name2 }}</td>
                                </tr>
                                <tr>
                                    <th>Last name:</th>
                                    <td>{{ personalData.last_name }}</td>
                                </tr>
                                <tr>
                                    <th>Preferred name:</th>
                                    <td>{{ personalData.preferred_name }}</td>
                                </tr>
                                <tr>
                                    <th>Date of birth:</th>
                                    <td>{{ new Date(personalData.date_of_birth).toLocaleString("en-GB", dateFormat) }}</td>
                                </tr>
                                <tr>
                                    <th>Gender:</th>
                                    <td>{{ personalData.gender.data.name ?? '' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>


                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="nationalityShow = !nationalityShow"
                >
                    <h1 class="text-1xl leading-10">Nationality</h1>
                    <icon :name="!nationalityShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !nationalityShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <table>
                            <tbody>
                                <tr>
                                    <th>Nationality (primary):</th>
                                    <td>{{ countryOfNationality }}</td>
                                </tr>
                                <tr>
                                    <th>Other Nationality:</th>
                                    <td>{{ countryOfOtherNationality }}</td>
                                </tr>
                                <tr>
                                    <th>Passport/EU identity card number:</th>
                                    <td>{{ applicationData.passport_number }}</td>
                                </tr>
                                <tr>
                                    <th>Country of Birth:</th>
                                    <td>{{ countryOfBirth }}</td>
                                </tr>
                                <tr>
                                    <th>Country of Domicile:</th>
                                    <td>{{ countryOfDomicile }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="contactDetailsShow = !contactDetailsShow"
                >
                    <h1 class="text-1xl leading-10">Contact Details</h1>
                    <icon :name="!contactDetailsShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !contactDetailsShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <div v-for="(contact, index) in contactData" :key="index">
                            <h1 class="text-1xl py-2 text-green-600 font-bold">Address {{ index + 1 }}</h1>
                            <table class="mb-8">
                                <tbody>
                                    <tr>
                                        <th>Address Line 1:</th>
                                        <td>{{ contact.address_line1 }}</td>
                                    </tr>
                                    <tr>
                                        <th>Address Line 2:</th>
                                        <td>{{ contact.address_line2 }}</td>
                                    </tr>
                                    <tr>
                                        <th>Address Line 3:</th>
                                        <td>{{ contact.address_line3 }}</td>
                                    </tr>
                                    <tr>
                                        <th>City / Town:</th>
                                        <td>{{ contact.city_town }}</td>
                                    </tr>
                                    <tr>
                                        <th>County / State:</th>
                                        <td>{{ contact.county_state }}</td>
                                    </tr>
                                    <tr>
                                        <th>Postal / Zip Code:</th>
                                        <td>{{ contact.post_zip_code }}</td>
                                    </tr>
                                    <tr>
                                        <th>Country: </th>
                                        <td>{{ filterCountryOfType(countryOfDomicileList, contact.country_id) }}</td>
                                    </tr>
                                    <tr>
                                        <th>Phone Number:</th>
                                        <td>{{ contact.phone_number }}</td>
                                    </tr>
                                    <tr>
                                        <th>Alternative Number:</th>
                                        <td>{{ contact.alt_number }}</td>
                                    </tr>
                                    <tr>
                                        <th>Address type:</th>
                                        <td>{{ filterContactDetailTypes(contact.type_id) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="educationDetailsShow = !educationDetailsShow"
                >
                    <h1 class="text-1xl leading-10">Education</h1>
                    <icon :name="!educationDetailsShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !educationDetailsShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <div v-for="(education, index) in priorEducation" :key="index">
                            <h1 class="text-1xl py-2 text-green-600 font-bold">Education record {{ index + 1 }}</h1>
                            <table class="mb-8">
                                <tbody>
                                    <tr>
                                        <th>Institution Name:</th>
                                        <td>{{ education.institution_name }}</td>
                                    </tr>
                                    <tr>
                                        <th>Qualification Type:</th>
                                        <td>{{ education.qualification_type }}</td>
                                    </tr>
                                    <tr>
                                        <th>Subject:</th>
                                        <td>{{ education.subject }}</td>
                                    </tr>
                                    <tr>
                                        <th>Final / Expected Result:</th>
                                        <td>{{ education.final_result }}</td>
                                    </tr>
                                    <tr>
                                        <th>Country:</th>
                                        <td>{{ filterCountryOfType(countryOfDomicileList, education.country_id) }}</td>
                                    </tr>
                                    <tr>
                                        <th>Language of instruction:</th>
                                        <td>{{ education.tuition_language }}</td>
                                    </tr>
                                    <tr>
                                        <th>Start Date:</th>
                                        <td>{{ new Date(education.start_date).toLocaleString("en-GB", dateFormat) }}</td>
                                    </tr>
                                    <tr>
                                        <th>End Date:</th>
                                        <td>{{ new Date(education.end_date).toLocaleString("en-GB", dateFormat) }}</td>
                                    </tr>
                                    <tr>
                                        <th>Notes or extenuating circumstances:</th>
                                        <td>{{ education.notes }}</td>
                                    </tr>
                                    <tr>
                                        <th>Was this a distance learning course?:</th>
                                        <td>{{ education.online_distance_learning === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="employmentDetailsShow = !employmentDetailsShow"
                >
                    <h1 class="text-1xl leading-10">Employment</h1>
                    <icon :name="!employmentDetailsShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !employmentDetailsShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <div v-for="(employment, index) in employmentHistory" :key="index">
                            <h1 class="text-1xl py-2 text-green-600 font-bold">Employment record {{ index + 1 }}</h1>
                            <table class="mb-8">
                                <tbody>
                                    <tr>
                                        <th>Employer:</th>
                                        <td>{{ employment.employer }}</td>
                                    </tr>
                                    <tr>
                                        <th>Position / Role:</th>
                                        <td>{{ employment.position }}</td>
                                    </tr>
                                    <tr>
                                        <th>Start date:</th>
                                        <td>{{ new Date(employment.start_date).toLocaleString("en-GB", dateFormat) }}</td>
                                    </tr>
                                    <tr>
                                        <th>End date:</th>
                                        <td><span v-if="employment.end_date">{{ new Date(employment.end_date).toLocaleString("en-GB", dateFormat) }}</span></td>
                                    </tr>
                                    <tr>
                                        <th>Address line 1:</th>
                                        <td>{{ employment.address_line1 }}</td>
                                    </tr>
                                    <tr>
                                        <th>Address line 2:</th>
                                        <td>{{ employment.address_line2 }}</td>
                                    </tr>
                                    <tr>
                                        <th>Address line 3:</th>
                                        <td>{{ employment.address_line3 }}</td>
                                    </tr>
                                    <tr>
                                        <th>City / Town:</th>
                                        <td>{{ employment.city_town }}</td>
                                    </tr>
                                    <tr>
                                        <th>County / State:</th>
                                        <td>{{ employment.county_state }}</td>
                                    </tr>
                                    <tr>
                                        <th>Postal / Zip Code:</th>
                                        <td>{{ employment.postcode }}</td>
                                    </tr>
                                    <tr>
                                        <th>Country:</th>
                                        <td>{{ filterCountryOfType(countryOfDomicileList, employment.country_id) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div v-if="requireWorkExperienceOrMaths" class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="workExperienceOrMathsShow = !workExperienceOrMathsShow"
                >
                    <h1 class="text-1xl leading-10">Work Experience and/or Mathematics Skills</h1>
                    <icon :name="!workExperienceOrMathsShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div v-if="requireWorkExperienceOrMaths" :class="{ hide: !workExperienceOrMathsShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <h1 class="text-1xl py-2 text-green-600 font-bold">Work Experience and/or Mathematics Skills</h1>
                        <table class="mb-8">
                            <tbody>
                                <tr>
                                    <th>Work Experience:</th>
                                    <td>{{ applicationData.workExperience ?? '' }}</td>
                                </tr>
                                <tr>
                                    <th>Mathematics Skills:</th>
                                    <td>{{ applicationData.mathematicsSkills ?? '' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="referencesShow = !referencesShow"
                >
                    <h1 class="text-1xl leading-10">Referees</h1>
                    <icon :name="!referencesShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !referencesShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <div v-for="(referee, index) in applicationData.referees.data" :key="index">
                            <h1 class="text-1xl py-2 text-green-600 font-bold">Referee record {{ index + 1 }}</h1>

                            <table class="mb-8">
                                <tbody>
                                    <tr>
                                        <th>Title:</th>
                                        <td>{{ referee.title }}</td>
                                    </tr>
                                    <tr>
                                        <th>Name:</th>
                                        <td>{{ referee.name ?? '' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Position / Role:</th>
                                        <td>{{ referee.position }}</td>
                                    </tr>
                                    <tr>
                                        <th>Organisation name:</th>
                                        <td>{{ referee.org }}</td>
                                    </tr>
                                    <tr>
                                        <th>Email:</th>
                                        <td>{{ referee.email }}</td>
                                    </tr>
                                    <tr>
                                        <th>Address line 1:</th>
                                        <td>{{ referee.address_line1 }}</td>
                                    </tr>
                                    <tr>
                                        <th>Address line 2:</th>
                                        <td>{{ referee.address_line2 }}</td>
                                    </tr>
                                    <tr>
                                        <th>Address line 3:</th>
                                        <td>{{ referee.address_line3 }}</td>
                                    </tr>
                                    <tr>
                                        <th>City / Town:</th>
                                        <td>{{ referee.city_town }}</td>
                                    </tr>
                                    <tr>
                                        <th>County / State:</th>
                                        <td>{{ referee.county_state }}</td>
                                    </tr>
                                    <tr>
                                        <th>Postal / Zip Code:</th>
                                        <td>{{ referee.post_zip_code }}</td>
                                    </tr>
                                    <tr>
                                        <th>Country:</th>
                                        <td>{{ filterCountryOfType(countryOfDomicileList, referee.country_id) }}</td>
                                    </tr>
                                    <tr>
                                        <th>Phone number:</th>
                                        <td>{{ referee.phone_number }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="statementShow = !statementShow"
                >
                    <h1 class="text-1xl leading-10">Personal Statement</h1>
                    <icon :name="!statementShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !statementShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <table>
                            <tbody>
                                <tr>
                                    <th colspan="2">Personal Statement</th>
                                </tr>
                                <tr>
                                    <th>Passion for the subject:</th>
                                    <td>{{ applicationData.personal_statement? applicationData.personal_statement['passion'] ?? '' : ''}}</td>
                                </tr>
                                <tr>
                                    <th>Motivation and commitment to their studies:</th>
                                    <td>{{ applicationData.personal_statement? applicationData.personal_statement['motivation'] ?? '' : ''}}</td>
                                </tr>
                                <tr>
                                    <th>Future aspirations in the chosen field:</th>
                                    <td>{{ applicationData.personal_statement? applicationData.personal_statement['aspiration'] ?? '' : ''}}</td>
                                </tr>
                                <tr>
                                    <th>Other relevant achievements:</th>
                                    <td>{{ applicationData.personal_statement? applicationData.personal_statement['other'] ?? '' : ''}}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="fundingDetailsShow = !fundingDetailsShow"
                >
                    <h1 class="text-1xl leading-10">Funding Details</h1>
                    <icon :name="!fundingDetailsShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !fundingDetailsShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <h1 class="text-1xl py-2 text-green-600 font-bold">Method of Funding</h1>
                        <table class="mb-8">
                            <tbody>
                                <tr>
                                    <th>Who will be paying your tuition fees and living costs?:</th>
                                    <td>{{ applicationData.method_of_funding }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <h1 class="text-1xl py-2 text-green-600 font-bold">LSHTM Scholarship</h1>
                        <table class="mb-8">
                            <tbody>
                                <tr>
                                    <th>Are you applying for a LSHTM scholarship?:</th>
                                    <td>{{ applicationData.lshtm_scholarship === 1 ? 'Yes' : 'No' }}</td>
                                </tr>
                                <tr>
                                    <th>Scholarship name:</th>
                                    <td>{{ applicationData.scholarship_name }}</td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-if="sponsorData.length">
                            <h1 class="text-1xl py-2 text-green-600 font-bold">Sponsor Details</h1>
                            <div v-for="(sponsor, index) in sponsorData" :key="index">
                                <h1 class="text-1xl py-2 text-green-600 font-bold">Sponsor {{ index + 1 }}</h1>
                                <table class="mb-8">
                                    <tbody>
                                        <tr>
                                            <th>Sponsor name and address:</th>
                                            <td>{{ sponsor.contact_details }}</td>
                                        </tr>
                                        <tr>
                                            <th>Amount of award:</th>
                                            <td>{{ sponsor.amount_of_award }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="documentationShow = !documentationShow"
                >
                    <h1 class="text-1xl leading-10">Supporting Documents</h1>
                    <icon :name="!documentationShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !documentationShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <div v-for="(document, index) in documentation" :key="index">
                            <h1 class="text-1xl py-2 text-green-600 font-bold">Document {{ index + 1 }}</h1>
                            <table class="mb-8">
                                <tbody>
                                    <tr>
                                        <th>Document Type:</th>
                                        <td>{{ document.type }}</td>
                                    </tr>
                                    <tr>
                                        <th>File name:</th>
                                        <td>
                                            <a class="underline text-blue-600 hover:text-blue-800 visited:text-purple-600 cursor-pointer"
                                               tabindex="-1" type="button"
                                               @click="download(document['reference'], document['original_filename'])"
                                            >
                                                {{ document.original_filename }}
                                            </a>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="equalOpportunitiesShow = !equalOpportunitiesShow"
                >
                    <h1 class="text-1xl leading-10">Equal Opportunities</h1>
                    <icon :name="!equalOpportunitiesShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !equalOpportunitiesShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <table class="table-lg">
                            <tbody>
                                <tr>
                                    <th>How did you hear about the School and the course to which you have applied?:</th>
                                    <td>{{ sourcesOfInterest }}</td>
                                </tr>
                                <tr>
                                    <th>To which ethnic group do you consider you belong?:</th>
                                    <td>{{ ethnicity }}</td>
                                </tr>
                                <tr>
                                    <th>Is your current gender identity the same as the gender originally assigned at birth?:</th>
                                    <td>{{ gender }}</td>
                                </tr>
                                <tr>
                                    <th>Please indicate your religious belief:</th>
                                    <td>{{ religiousBelief }}</td>
                                </tr>
                                <tr>
                                    <th>Please indicate your sexual orientation:</th>
                                    <td>{{ sexualOrientation }}</td>
                                </tr>
                                <tr>
                                    <th>Do you have any unspent criminal convictions?:</th>
                                    <td>{{ equalOpportunities.criminal_conviction === 1 ? 'Yes' : 'No' }}</td>
                                </tr>
                                <tr>
                                    <th>Please indicate any disabilities or special needs that may require special arrangements or facilities:</th>
                                    <td>{{ disability }}</td>
                                </tr>
                                <template v-if="!hideDisabilitySection">
                                    <tr>
                                        <th>Please provide further information:</th>
                                        <td>{{ equalOpportunities.disability_details }}</td>
                                    </tr>
                                    <tr>
                                        <th>How would you describe your disability? Please briefly explain how your disability or medical condition affects you:</th>
                                        <td>{{ equalOpportunities.disability_effect }}</td>
                                    </tr>
                                    <tr>
                                        <th>
                                            Please describe any support received in the past and/or required now, e.g. information in large print, signer, extra time in
                                            examinations. Please give details of all requirements:
                                        </th>
                                        <td>{{ equalOpportunities.disability_support_required }}</td>
                                    </tr>
                                    <tr>
                                        <th>Do you require level access to get into buildings?:</th>
                                        <td>{{ equalOpportunities.disability_level_building_access === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Do you require lower level lift buttons?:</th>
                                        <td>{{ equalOpportunities.disability_lower_buttons === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Can you climb stairs?:</th>
                                        <td>{{ equalOpportunities.disability_climb_stairs === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Can you use an adapted lift independently?:</th>
                                        <td>{{ equalOpportunities.disability_adapted_lift === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Can you find your way in unfamiliar buildings?:</th>
                                        <td>{{ equalOpportunities.disability_unfamiliar_buildings === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Are you able to open weighted doors independently?:</th>
                                        <td>{{ equalOpportunities.disability_weighted_doors === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Are you able to hear a fire alarm?:</th>
                                        <td>{{ equalOpportunities.disability_hear_alarms === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Are you able to use stairs unaided if the alarm sounds?:</th>
                                        <td>{{ equalOpportunities.disability_use_stairs_unaided === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Please provide any further relevant details:</th>
                                        <td>{{ equalOpportunities.disability_use_stairs_details }}</td>
                                    </tr>
                                    <tr>
                                        <th>Do you require named people to assist you in leaving a building?:</th>
                                        <td>{{ equalOpportunities.disability_named_people_egress === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>If you require adapted accommodation please specify your requirements:</th>
                                        <td>{{ equalOpportunities.disability_accommodation }}</td>
                                    </tr>
                                    <tr>
                                        <th>Do you need personal care assistance?:</th>
                                        <td>{{ equalOpportunities.disability_personal_care_assistance === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Please indicate any support or facilities, which may be required within the Library:</th>
                                        <td>{{ equalOpportunities.disability_library }}</td>
                                    </tr>
                                    <tr>
                                        <th>If you have any particular requirements for adapted computer facilities please indicate:</th>
                                        <td>{{ equalOpportunities.disability_computer }}</td>
                                    </tr>
                                    <tr>
                                        <th>
                                            Please specify any leisure facilities which particularly interest you and any access or facilitation which may be
                                            required:
                                        </th>
                                        <td>{{ equalOpportunities.disability_leisure }}</td>
                                    </tr>
                                    <tr>
                                        <th>Please describe any other facilitation or access you feel is required:</th>
                                        <td>{{ equalOpportunities.disability_other }}</td>
                                    </tr>
                                    <tr>
                                        <th>
                                            We recommend that disabled applicants visit LSHTM specifically to look at access and facilities offered. Please indicate
                                            whether you think a visit is possible:
                                        </th>
                                        <td>{{ equalOpportunities.disability_visit_possible === 1 ? 'Yes' : 'No' }}</td>
                                    </tr>
                                    <tr>
                                        <th>Consent to share:</th>
                                        <td>
                                            {{
                                                equalOpportunities.disability_consent_to_share === 1 ? 'I consent to appropriate and relevant information being disclosed to my department or other University services in order to make the adjustments I require' : 'I do not consent to my disclosed information being shared (this may prevent or limit support arrangements)'
                                            }}
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1"
                     @click="contextualDataShow = !contextualDataShow"
                >
                    <h1 class="text-1xl leading-10">Contextual Data</h1>
                    <icon :name="!contextualDataShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !contextualDataShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <section-render v-if="loading" :application-id="applicationRef" :section="sections.filter((section => section.key === 'contextual-admissions'))[0]" :section-data="contextualData" :readonly="true" />
                    </div>
                </div>

                <div class="bg-white border border-gray-300 cursor-pointer hover:bg-gray-200 flex justify-between px-8 mt-1" @click="paymentShow = !paymentShow">
                    <h1 class="text-1xl leading-10">Payment Information</h1>
                    <icon :name="!paymentShow ? 'cheveron-right' : 'cheveron-down'" class="fill-current w-7 h-10 ml-2" />
                </div>
                <div :class="{ hide: !paymentShow }" class="bg-white border border-t-0 border-gray-300 overflow-hidden">
                    <div v-if="loading" class="p-8 mt-2 text-left">
                        <div v-for="(payment, index) in paymentData" :key="index">
                            <h1 class="text-1xl py-2 text-green-600 font-bold">Payment record {{ index + 1 }}</h1>
                            <table class="mb-8">
                                <tbody>
                                    <tr>
                                        <th>Payment Type:</th>
                                        <td>{{ payment.type_string }}</td>
                                    </tr>
                                    <tr>
                                        <th>Amount:</th>
                                        <td>£{{ payment.amount }}</td>
                                    </tr>
                                    <tr>
                                        <th>Payment Status:</th>
                                        <td>{{ payment.application_fee_status }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/shared/Icon'
import SectionRender from './Details/SectionRender'
import DocumentRequestResponseForm from "@/shared/forms/DocumentRequestResponseForm";

export default {
    name: 'Application',
    metaInfo() {
        return {
            title: 'Application',
        }
    },
    components: {
      SectionRender,
      DocumentRequestResponseForm,
      Icon,
    },
    data() {
        return {

            form: ({
                reference: this.applicationRef,
                document_type: null,
                file: null,
            }),
            applicationRef: String,
            personalDetailsShow: false,
            nationalityShow: false,
            contactDetailsShow: false,
            statementShow: false,
            programmeDetailsShow: true,
            educationDetailsShow: false,
            employmentDetailsShow: false,
            fundingDetailsShow: false,
            referencesShow: false,
            documentationShow: false,
            equalOpportunitiesShow: false,
            contextualDataShow: false,
            paymentShow: false,
            workExperienceOrMathsShow: false,
            applicationData: [],
            personalData: {
                first_name: '',
                last_name: '',
            },
            contactData: [],
            contextualData: {},
            programmeData: [],
            priorEducation: [],
            employmentHistory: [],
            equalOpportunities: [],
            documentation: [],
            sponsorData: [],
            paymentData: [],
            contactDetailTypes: [],
            loading: false,
            sourcesOfInterest: '',
            ethnicity: '',
            gender: '',
            religiousBelief: '',
            sexualOrientation: '',
            disability: '',
            hideDisabilitySection: false,
            dateFormat: {year: 'numeric', month: 'long', day: 'numeric'},
            expandAll: false,
            successMessage: [],
            serverError: null,

        }
    },
    computed: {
        firstChoiceType: {
            get: function () {
                const programmeTypes = this.$store.getters.programmeTypesList

                if (programmeTypes !== undefined) {
                    if (programmeTypes.data.length > 0 && this.$store.getters.applicationDetails['data']['first_choice_type']) {
                        return programmeTypes.data.filter(programmeType => programmeType['reference'] === this.$store.getters.applicationDetails['data']['first_choice_type'])[0]
                    }
                }
                return {name: '', num_references_required: 2}
            },
        },
        firstChoice: {
            get: function () {
                const programmes = this.$store.getters.programmesList

                if (programmes !== undefined) {
                    if (programmes.data.length > 0 && this.$store.getters.applicationDetails['data']['first_choice'] !== null) {
                        return programmes.data.filter(programme => programme['reference'] === this.$store.getters.applicationDetails['data']['first_choice'])[0]
                    }
                }
                return {name: '', programme_title: '', sits_ipo_ayrc: '', moa_name: ''}
            },
        },
        applicationDetails() {
            return this.$store.getters.applicationDetails
        },
        programmesList() {
            return this.$store.getters.programmesList
        },
        ethnicityList() {
            return this.$store.getters.ethnicityList
        },
        disabilityList() {
            return this.$store.getters.disabilityList
        },
        religiousBeliefList() {
            return this.$store.getters.religiousBeliefList
        },
        sexualOrientationList() {
            return this.$store.getters.sexualOrientationList
        },
        genderIdentityList() {
            return this.$store.getters.genderIdentityList
        },
        sourcesOfInterestList() {
            return this.$store.getters.sourcesOfInterestList
        },
        sections() {
          return this.$store.getters.sections
        },
        contactDetailTypesList() {
            return this.$store.getters.contactDetailTypesList
        },
        countryOfDomicileList() {
            return this.$store.getters.countriesOfDomicileList
        },
        countriesOfBirthList() {
            return this.$store.getters.countriesOfBirthList
        },
        programmeTypesList() {
            return this.$store.getters.programmeTypesList
        },
        proposedStartDateList() {
            return this.$store.getters.proposedStartDateList
        },
        nationalityList() {
            return this.$store.getters.nationalityList
        },
        countryOfDomicile() {
            return this.filterCountryOfType(this.countryOfDomicileList, this.applicationData.country_of_domicile_id)
        },
        countryOfBirth() {
            return this.filterCountryOfType(this.countriesOfBirthList, this.applicationData.country_of_birth_id)
        },
        countryOfNationality() {
            return this.filterCountryOfType(this.nationalityList, this.applicationData.nationality_id)
        },
        countryOfOtherNationality() {
            return this.filterCountryOfType(this.nationalityList, this.applicationData.dual_nationality_id)
        },
        requireWorkExperienceOrMaths() {
            if (this.programmeData === undefined || this.programmeData === null || this.programmeData.data === undefined || this.programmeData.data === null)
                return false;

            let filtered = this.programmeData.data.filter(programme => programme.requireWorkExperience || programme.requireMathsSkills);
            return filtered !== undefined && filtered !== null && filtered.length > 0;
        }
    },
    watch: {
        applicationDetails(watchedData) {
            this.applicationRef = watchedData.data.reference
            this.applicationData = watchedData.data
            this.personalData = watchedData.data.user.data
            this.contactData = watchedData.data.contactDetails.data
            this.programmeData = watchedData.data.applicationProgrammes
            this.priorEducation = watchedData.data.priorEducation.data
            this.employmentHistory = watchedData.data.employmentHistory.data
            this.contextualData = JSON.parse(JSON.stringify(watchedData.data.application_data))
            this.equalOpportunities = watchedData.data.equalOpportunities.data
            this.documentation = watchedData.data.documents.data
            this.sponsorData = watchedData.data.applicationSponsors.data
            this.paymentData = watchedData.data.payments.data
            this.loading = true
        },
        programmesList(data) {
            this.programmes = data
        },
        ethnicityList(data) {
            data.forEach((item) => {
                if (this.equalOpportunities.ethnic_origin === item.code) {
                    this.ethnicity = item.name
                }
            })
        },
        disabilityList(data) {
            if (this.equalOpportunities.disabilities === '00' || this.equalOpportunities.disabilities === '97') {
                this.hideDisabilitySection = true
            }
            data.forEach((item) => {
                if (this.equalOpportunities.disabilities === item.code) {
                    this.disability = item.name
                }
            })
        },
        religiousBeliefList(data) {
            data.forEach((item) => {
                if (this.equalOpportunities.religious_belief === item.code) {
                    this.religiousBelief = item.name
                }
            })
        },
        sexualOrientationList(data) {
            data.forEach((item) => {
                if (this.equalOpportunities.sexual_orientation === item.code) {
                    this.sexualOrientation = item.name
                }
            })
        },
        genderIdentityList(data) {
            data.forEach((item) => {
                if (this.equalOpportunities.gender_identity === item.code) {
                    this.gender = item.name
                }
            })
        },
        sourcesOfInterestList(data) {
            data.forEach((soi) => {
                if (this.equalOpportunities.sources_of_interest === soi.code) {
                    this.sourcesOfInterest = soi.name
                }
            })
        },
        contactDetailTypesList(data) {
            this.contactDetailTypes = data
        },
        nationalityList(data) {
            data.forEach((country) => {
                if (this.applicationData.nationality_id === country.reference) {
                    this.getNationality = country.name
                } else if (this.applicationData.dual_nationality_id === country.reference) {
                    this.getOtherNationality = country.name
                }
            })
        },
    },
    created() {
        this.loadApplication()
    },
    methods: {
      filterCountryOfType(filterList, filterReference)
      {
        const filteredCountry = filterList.filter(item => item.reference === filterReference)
        return filteredCountry?.[0]?.name ?? ''
      },
        filterContactDetailTypes(filterReference)
        {
            return this.contactDetailTypes.filter(contact => contact.reference === filterReference)[0]['name'] ?? ''
        },
        download(reference, fileName) {
            const formData = new FormData()
            formData.append('reference', this.form.reference)
            formData.append('file', this.form.file)

            this.$store.dispatch('downloadDocument', {
                applicationRef: this.applicationRef,
                documentRef: reference,
                file: formData,
                fileName: fileName,
                referrerRef: this.$route.params.id,
                token: this.$route.params.token,
            })
                .then(() => {
                    this.successMessage2 = 'Document downloaded'
                    this.serverError2 = ''
                })
                .catch(() => {
                    this.successMessage2 = ''
                    this.serverError2 = 'Something went wrong, please try again'
                })

        },
        loadApplication() {
            this.$store.dispatch('retrieveSingleApplication', {
                applicationRef: this.$route.params.id,
            })
        },
        expand() {
            if (this.expandAll) {
                this.expandAll = false
            } else {
                this.expandAll = true
            }
            this.personalDetailsShow = this.expandAll
            this.nationalityShow = this.expandAll
            this.contactDetailsShow = this.expandAll
            this.statementShow = this.expandAll
            this.programmeDetailsShow = this.expandAll
            this.educationDetailsShow = this.expandAll
            this.employmentDetailsShow = this.expandAll
            this.fundingDetailsShow = this.expandAll
            this.referencesShow = this.expandAll
            this.documentationShow = this.expandAll
            this.equalOpportunitiesShow = this.expandAll
            this.contextualDataShow = this.expandAll
            this.paymentShow = this.expandAll
            this.workExperienceOrMathsShow = this.expandAll
        },
        sendReminderEmailToReferee(refereeReference, messageIndex) {
            this.loading = true
            this.$store.dispatch('sendReminderEmailToReferee', {
                reference: refereeReference,
            })
                .then((response) => {
                    this.loading = false
                    this.successMessage[messageIndex] = response.data
                    this.serverError = ''
                    this.loadApplication()
                })
                .catch(error => {
                    console.log(error)
                    this.loading = false
                    this.successMessage = []
                    this.serverError = error.response.data
                })
            this.formDirty = false
            this.loading = false
        },
    },
}
</script>
<style scoped>
table {
    width: 100%;
    border: 1px solid #eee;
}

table tr {
    border: 1px solid #eee;
}

table td {
    padding: 5px;
    border: 1px solid #eee;
}

table th {
    padding: 5px;
}

@media screen and (min-width: 1201px) {
    table th {
        width: 35%;
    }

    table.table-lg th {
        width: 60%;
    }
}

.hide {
    display: none !important;
}
</style>